import { getSideForPositionAdjustment, normalizeAdjustment, calculateAdjustmentQuantity } from '../utils';
import type { BasketTradingAction } from './actions';
import { log } from './log';
import type { BasketTradingState, BasketOrder } from './types';
import { isEqual } from 'es-toolkit';

export const initialState: BasketTradingState = {
    type: 'quantity',
    label: undefined,
    defaults: {
        exDestination: 'THINKALPHA',
        route: 'SOR',
        account: undefined as unknown as string,
        quantity: 100,
        type: 'market',
        side: 'buy',
        direction: 'increase',
        duration: 'day',
        multiplier: 1,
        notionalAmount: 10_000,
        weightBasis: 'equal',
        notionalType: 'net',
        adjustment: 0,
    },
    positionsAccount: undefined,
    basket: [],
    ordersLimit: 1000,
    ordersLimitReached: false,
    weightBasis: 'equal',
    notionalAmount: undefined,
    notionalType: 'net',
};

/**
 * Calculate dirty fields for an order by comparing against defaults
 * Excludes quantity field for notional and position adjustment baskets
 * For position adjustment baskets, normalizes adjustment before comparison
 */
const calculateDirtyFields = (
    order: BasketOrder,
    defaults: BasketTradingState['defaults'],
    basketType: BasketTradingState['type'],
): string[] => {
    const shouldSkip = (key: string) =>
        (key === 'quantity' || key === 'adjustmentQuantity') &&
        (basketType === 'notional' || basketType === 'positionAdjustment');

    return Object.keys(order).filter((key) => {
        if (!(key in defaults) || shouldSkip(key)) {
            return false;
        }

        // For position adjustment baskets, compare normalized adjustment values
        if (basketType === 'positionAdjustment' && key === 'adjustment' && order.adjustment !== undefined) {
            return normalizeAdjustment(order.adjustment) !== normalizeAdjustment(defaults.adjustment);
        }

        return order[key] !== defaults[key as keyof typeof defaults];
    });
};

const clearBasketState = (state: BasketTradingState): BasketTradingState => {
    return {
        ...state,
        label: undefined,
        basket: [],
        ordersLimitReached: false,
    };
};

/**
 * Normalize an order based on basket type
 * - Weighted baskets: ensures multiplier is at least 1, sets direction based on side
 * - Position adjustment baskets: normalizes adjustment value, sets direction, and determines side from direction
 * - Notional baskets: sets direction based on side, sets weight to 0 for custom weight basis
 * - Calculates dirty fields by comparing against defaults
 */
const normalizeOrderForBasketType = (
    order: BasketOrder,
    basketType: BasketTradingState['type'],
    weightBasis: BasketTradingState['weightBasis'],
    defaults: BasketTradingState['defaults'],
): BasketOrder => {
    let normalizedOrder = { ...order };

    if (basketType === 'weighted') {
        if (normalizedOrder.multiplier === undefined || normalizedOrder.multiplier < 1) {
            normalizedOrder.multiplier = 1;
        }
    }

    if (basketType === 'positionAdjustment') {
        if (normalizedOrder.adjustment === undefined) {
            normalizedOrder.adjustment = 0;
        }

        if (normalizedOrder.direction === undefined) {
            normalizedOrder.direction = defaults.direction ?? 'increase';
        }

        normalizedOrder.adjustment = normalizeAdjustment(normalizedOrder.adjustment);

        normalizedOrder.adjustmentQuantity = calculateAdjustmentQuantity(
            normalizedOrder.quantity,
            normalizedOrder.adjustment,
        );

        normalizedOrder.side = getSideForPositionAdjustment(normalizedOrder.quantity, normalizedOrder.direction);
    }

    if (basketType === 'notional' || basketType === 'weighted') {
        normalizedOrder.direction = normalizedOrder.side === 'buy' ? 'increase' : 'decrease';
    }

    if (basketType === 'notional' && weightBasis === 'custom' && normalizedOrder.weight === undefined) {
        normalizedOrder = { ...normalizedOrder, weight: 0 };
    }

    normalizedOrder.dirty = calculateDirtyFields(normalizedOrder, defaults, basketType);

    return normalizedOrder;
};

const updateOrder = (state: BasketTradingState, update: BasketOrder) => {
    const orderIndex = state.basket.findIndex((i) => isEqual(i.instrument, update.instrument));

    if (orderIndex === -1 || isEqual(state.basket[orderIndex], update)) {
        return state;
    }

    // Check if weight was manually changed
    const originalOrder = state.basket[orderIndex];
    const weightChanged =
        state.type === 'notional' && update.weight !== undefined && originalOrder.weight !== update.weight;

    // If weight was manually changed and we're in equal mode, switch to custom mode
    const newWeightBasis = weightChanged && state.weightBasis === 'equal' ? 'custom' : state.weightBasis;

    return {
        ...state,
        weightBasis: newWeightBasis,
        basket: state.basket.map((order, index) =>
            orderIndex === index
                ? normalizeOrderForBasketType({ ...order, ...update }, state.type, newWeightBasis, state.defaults)
                : order,
        ),
    };
};

export function basketTradingReducer(state: BasketTradingState, action: BasketTradingAction): BasketTradingState {
    if (TRACE) {
        log.trace({
            message: `basketTradingReducer action ${action.type}`,
            // Uncommenting this WILL cause performance issues due to large action objects
            /* action */
        });
    }

    switch (action.type) {
        case 'INITIALIZE_STATE': {
            return {
                ...initialState,
            };
        }

        case 'SET_BASKET_TYPE': {
            const type = action.value;
            if (state.type === type) {
                return state;
            }

            return {
                ...clearBasketState(state),
                type,
            };
        }

        case 'SET_BASKET_LABEL': {
            const label = action.value;
            if (state.label === label) {
                return state;
            }

            return {
                ...state,
                label,
            };
        }

        case 'SET_DEFAULTS': {
            if (isEqual(state.defaults, action.value)) {
                return state;
            }

            // Clear basket if account changed (except for positionAdjustment which manages positions separately)
            if (state.defaults.account !== action.value.account && state.type !== 'positionAdjustment') {
                return {
                    ...clearBasketState(state),
                    defaults: { ...action.value },
                };
            }

            const isPositionAdjustment = state.type === 'positionAdjustment';
            const shouldSkip = (key: string) => {
                // Only skip quantity for position adjustment baskets
                return key === 'quantity' && isPositionAdjustment;
            };

            let newState = {
                ...state,
                defaults: { ...action.value },
            };

            for (const order of state.basket) {
                const changedDefaults: Record<string, any> = {};

                for (const key of Object.keys(action.value) as Array<keyof typeof action.value>) {
                    if (state.defaults[key] === action.value[key] || shouldSkip(key)) {
                        continue;
                    }
                    changedDefaults[key] = action.value[key];
                }

                const updatedOrder = { ...order, ...changedDefaults };

                // For positionAdjustment baskets, normalize adjustment and update side when direction changes
                if (isPositionAdjustment && 'direction' in changedDefaults && 'adjustment' in updatedOrder) {
                    const direction = changedDefaults.direction ?? 'increase';

                    updatedOrder.adjustment = normalizeAdjustment(updatedOrder.adjustment);
                    updatedOrder.side = getSideForPositionAdjustment(updatedOrder.quantity, direction);
                    updatedOrder.direction = direction;
                }

                updatedOrder.dirty = calculateDirtyFields(updatedOrder, action.value, state.type);

                if (!isEqual(order, updatedOrder)) {
                    newState = updateOrder(newState, updatedOrder);
                }
            }
            return newState;
        }

        case 'RESET_BASKET': {
            return clearBasketState(state);
        }

        case 'ADD_ORDER': {
            const order = action.order;
            if (state.basket.find((i) => isEqual(i.instrument, order.instrument))) {
                return state;
            }

            // Normalize order based on basket type
            const normalizedOrder = normalizeOrderForBasketType(order, state.type, state.weightBasis, state.defaults);

            let basket = [...state.basket, normalizedOrder];

            // If notional basket with equal distribution, redistribute weights
            if (state.type === 'notional' && state.weightBasis === 'equal') {
                const baseWeight = Math.floor(10000 / basket.length) / 100;
                const totalBaseWeight = baseWeight * basket.length;
                const remainder = Math.round((100 - totalBaseWeight) * 100) / 100;

                basket = basket.map((o, index) => {
                    const weight = index === 0 ? baseWeight + remainder : baseWeight;
                    const roundedWeight = Math.round(weight * 100) / 100;
                    return {
                        ...o,
                        weight: roundedWeight,
                    };
                });

                if (TRACE) {
                    log.trace('Redistributed weights after adding order', {
                        basketLength: basket.length,
                        baseWeight,
                        remainder,
                        totalWeight: basket.reduce((sum, o) => sum + (o.weight ?? 0), 0),
                    });
                }
            }

            return {
                ...state,
                basket,
                ordersLimitReached: basket.length >= state.ordersLimit,
            };
        }

        case 'ADD_ORDERS_BATCH': {
            // Filter out orders that already exist in the basket
            const newOrders = action.orders.filter(
                (order) => !state.basket.find((i) => isEqual(i.instrument, order.instrument)),
            );

            if (newOrders.length === 0) {
                return state;
            }

            // Normalize all new orders based on basket type
            const normalizedOrders = newOrders.map((order) =>
                normalizeOrderForBasketType(order, state.type, state.weightBasis, state.defaults),
            );

            let basket = [...state.basket, ...normalizedOrders];

            // If notional basket with equal distribution, redistribute weights once for all orders
            if (state.type === 'notional' && state.weightBasis === 'equal') {
                const baseWeight = Math.floor(10000 / basket.length) / 100;
                const totalBaseWeight = baseWeight * basket.length;
                const remainder = Math.round((100 - totalBaseWeight) * 100) / 100;

                basket = basket.map((o, index) => {
                    const weight = index === 0 ? baseWeight + remainder : baseWeight;
                    const roundedWeight = Math.round(weight * 100) / 100;
                    return {
                        ...o,
                        weight: roundedWeight,
                    };
                });

                if (TRACE) {
                    log.trace('Redistributed weights after adding batch orders', {
                        basketLength: basket.length,
                        newOrdersCount: newOrders.length,
                        baseWeight,
                        remainder,
                        totalWeight: basket.reduce((sum, o) => sum + (o.weight ?? 0), 0),
                    });
                }
            }

            return {
                ...state,
                basket,
                ordersLimitReached: basket.length >= state.ordersLimit,
            };
        }

        case 'REMOVE_ORDER': {
            let basket = state.basket.filter((i) => !isEqual(i.instrument, action.order.instrument));

            // If notional basket with equal distribution, redistribute weights after removal
            if (state.type === 'notional' && state.weightBasis === 'equal' && basket.length > 0) {
                const baseWeight = Math.floor(10000 / basket.length) / 100;
                const totalBaseWeight = baseWeight * basket.length;
                const remainder = Math.round((100 - totalBaseWeight) * 100) / 100;

                basket = basket.map((o, index) => {
                    const weight = index === 0 ? baseWeight + remainder : baseWeight;
                    const roundedWeight = Math.round(weight * 100) / 100;
                    return {
                        ...o,
                        weight: roundedWeight,
                    };
                });

                if (TRACE) {
                    log.trace('Redistributed weights after removing order', {
                        basketLength: basket.length,
                        baseWeight,
                        remainder,
                        totalWeight: basket.reduce((sum, o) => sum + (o.weight ?? 0), 0),
                    });
                }
            }

            return {
                ...state,
                basket,
                ordersLimitReached: basket.length >= state.ordersLimit,
            };
        }

        case 'UPDATE_ORDER': {
            return updateOrder(state, action.order);
        }

        case 'REDISTRIBUTE_WEIGHTS': {
            // Only redistribute if basket is not empty and we're in notional mode
            if (state.basket.length === 0 || state.type !== 'notional') {
                return state;
            }

            // Calculate equal weight with 2 decimal precision
            // Using Math.floor to ensure we don't exceed 100%
            const baseWeight = Math.floor(10000 / state.basket.length) / 100;
            const totalBaseWeight = baseWeight * state.basket.length;
            // Calculate remainder to add to first order to ensure total = 100%
            const remainder = Math.round((100 - totalBaseWeight) * 100) / 100;

            const basket = state.basket.map((order, index) => {
                const weight = index === 0 ? baseWeight + remainder : baseWeight;
                // Round to 2 decimal places to avoid floating point precision issues
                const roundedWeight = Math.round(weight * 100) / 100;
                return {
                    ...order,
                    weight: roundedWeight,
                };
            });

            if (TRACE) {
                log.trace('Redistributed weights', {
                    basketLength: state.basket.length,
                    baseWeight,
                    remainder,
                    totalWeight: basket.reduce((sum, o) => sum + (o.weight ?? 0), 0),
                });
            }

            return {
                ...state,
                basket,
                weightBasis: 'equal', // Set to equal when redistributing
            };
        }

        case 'SET_WEIGHT_BASIS': {
            const newWeightBasis = action.value;

            // If setting to equal mode, redistribute weights
            if (newWeightBasis === 'equal' && state.type === 'notional' && state.basket.length > 0) {
                const baseWeight = Math.floor(10000 / state.basket.length) / 100;
                const totalBaseWeight = baseWeight * state.basket.length;
                const remainder = Math.round((100 - totalBaseWeight) * 100) / 100;

                const basket = state.basket.map((order, index) => {
                    const weight = index === 0 ? baseWeight + remainder : baseWeight;
                    const roundedWeight = Math.round(weight * 100) / 100;
                    return {
                        ...order,
                        weight: roundedWeight,
                    };
                });

                if (TRACE) {
                    log.trace('Redistributed weights on setting equal mode', {
                        basketLength: state.basket.length,
                        baseWeight,
                        remainder,
                        totalWeight: basket.reduce((sum, o) => sum + (o.weight ?? 0), 0),
                    });
                }

                return {
                    ...state,
                    weightBasis: newWeightBasis,
                    basket,
                };
            }

            return {
                ...state,
                weightBasis: newWeightBasis,
            };
        }

        case 'SET_NOTIONAL_AMOUNT': {
            return {
                ...state,
                notionalAmount: action.value,
            };
        }

        case 'SET_NOTIONAL_TYPE': {
            return {
                ...state,
                notionalType: action.value,
            };
        }

        case 'SET_POSITIONS_ACCOUNT': {
            if (state.positionsAccount === action.value) {
                return state;
            }

            // Only update positionsAccount - the component will trigger a position reload
            // which handles clearing the basket and setting the new label
            return {
                ...state,
                positionsAccount: action.value,
            };
        }

        default:
            return state;
    }
}
